import { useState, type FormEvent, type ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '@/services/authService';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/store/language-store';
import { useTheme } from '@/components/ThemeProvider';
import { cn } from '@/lib/utils';
import LogoDark from '../assets/logo-dark.svg?url';
import LogoLight from '../assets/logo-light.svg?url';
import { SocialLoginButton } from '../components/auth/SocialLoginButton';
import { supabase } from '@/lib/supabase';

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);

  // Check for existing session on component mount
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        console.log('Login page: Checking auth state...');
        
        // Check if we have a valid session
        const { data, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error checking session:', error)
          return
        }
        
        if (data.session) {
          console.log('User already has an active session, redirecting to dashboard');
          // Clear any potential authentication flags to prevent loops
          sessionStorage.removeItem('auth_completed');
          sessionStorage.removeItem('auth_attempt_count');
          sessionStorage.removeItem('auth_in_progress');
          
          // Set a flag to indicate we've handled the redirect to prevent loops
          localStorage.setItem('last_auth_redirect', Date.now().toString());
          
          // Check if we were redirected from another page (caused by a refresh)
          const redirectPath = sessionStorage.getItem('authRedirectPath');
          
          // Delay redirection to allow auth store to sync
          setTimeout(() => {
            if (redirectPath && !redirectPath.includes('/auth/')) {
              console.log('Returning to original page after refresh:', redirectPath);
              sessionStorage.removeItem('authRedirectPath');
              navigate(redirectPath);
            } else {
              navigate('/app/dashboard');
            }
          }, 500);
        } else {
          // No session found, stay on login page and clear attempt counter
          sessionStorage.removeItem('auth_attempt_count');
        }
      } catch (error) {
        console.error('Error checking authentication state:', error);
        // Clear attempt counter on error
        sessionStorage.removeItem('auth_attempt_count');
      }
    };
    
    checkAuthState();
  }, [navigate]);

  // Check for login error parameters in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) {
      const errorMsg = params.get('error_description') || 'Authentication failed';
      toast.error(t('auth.errors.loginFailed'), {
        description: errorMsg
      });
    }
    
    if (params.get('reset') === 'true') {
      console.log('Login page loaded with reset flag, clearing session storage');
      sessionStorage.clear();
    }
  }, [t]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.errors.fillAllFields'), {
        description: t('auth.errors.fillAllFields')
      });
      return;
    }

    setLoading(true);
    try {
      const response = await authService.loginWithEmail(email, password);
      if (response.success && response.user) {
        toast.success(t('auth.success.signedIn'), {
          description: t('auth.success.signedIn')
        });
        window.location.href = '/app/dashboard';
      } else {
        toast.error(t('auth.errors.loginFailed'), {
          description: t(response.error || 'auth.errors.generic')
        });
      }
    } catch (error) {
      console.log('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'auth.errors.generic';
      toast.error(t('auth.errors.loginFailed'), {
        description: t(errorMessage)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    try {
      // Mark authentication as in progress to help track state
      sessionStorage.setItem('auth_in_progress', 'true');
      // Reset any previous attempt counters
      sessionStorage.removeItem('auth_attempt_count');
      
      // Store the intended redirect destination for after successful auth
      localStorage.setItem('post_auth_redirect', '/app/dashboard');
      console.log('Stored post-auth redirect destination: /app/dashboard');
      
      // Log detailed location info to help debug redirect issues
      console.log('Current location before Google sign-in:');
      console.log('- href:', window.location.href);
      console.log('- origin:', window.location.origin);
      console.log('- protocol:', window.location.protocol);
      console.log('- host:', window.location.host);
      console.log('- pathname:', window.location.pathname);
      
      console.log('Starting Google sign-in flow...');
      const response = await authService.loginWithGoogle();
      if (!response.success) {
        toast.error(t('auth.errors.loginFailed'), {
          description: t(response.error || 'auth.errors.generic')
        });
      }
      // On success, user will be redirected by Supabase
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      const errorMessage = error instanceof Error ? error.message : 'auth.errors.generic';
      toast.error(t('auth.errors.loginFailed'), {
        description: t(errorMessage)
      });
      // Clear the in-progress flag if there's an error
      sessionStorage.removeItem('auth_in_progress');
    } finally {
      // Set a timeout to reset loading since the redirect will happen if successful
      setTimeout(() => {
        setSocialLoading(null);
      }, 5000); // 5 second timeout as a fallback
    }
  };

  const handleAppleLogin = async () => {
    try {
      setSocialLoading('apple');
      console.log('Starting Apple Sign-In process...');
      
      // Clear any previous Apple auth errors from session storage
      sessionStorage.removeItem('appleAuthError');
      
      const response = await authService.loginWithApple();
      
      if (!response.success) {
        toast.error(t('auth.errors.appleLoginFailed'), {
          description: t(response.error || 'auth.errors.generic')
        });
      }
      // Note: On success, the user will be redirected to the callback URL
      // so we don't need to handle success here
    } catch (error) {
      console.error('Unexpected error during Apple Sign-In:', error);
      const errorMessage = error instanceof Error ? error.message : 'auth.errors.generic';
      
      // Check for specific error types
      let translationKey = 'auth.errors.generic';
      if (errorMessage.includes('popup-closed-by-user')) {
        translationKey = 'auth.errors.popupClosed';
      } else if (errorMessage.includes('popup-blocked')) {
        translationKey = 'auth.errors.popupBlocked';
      } else if (errorMessage.includes('account-exists-with-different-credential')) {
        translationKey = 'auth.errors.accountExistsWithDifferentCredential';
      } else if (errorMessage.includes('invalid-credential')) {
        translationKey = 'auth.errors.audienceMismatch';
      } else if (errorMessage.includes('missing-or-invalid-nonce')) {
        translationKey = 'auth.errors.duplicateCredential';
        localStorage.clear();
      }
      
      toast.error(t('auth.errors.appleLoginFailed'), {
        description: t(translationKey)
      });
    } finally {
      setSocialLoading(null);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);

  return (
    <div className={cn(
      "min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
            <img 
              src={theme === 'dark' ? LogoDark : LogoLight}
              alt="Flows.Cash Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {t('auth.welcome')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 py-8 px-4 shadow-sm sm:rounded-xl sm:px-10">
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-3">
              {/* Google Sign-In Button */}
              <SocialLoginButton
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                }
                label={t('auth.actions.continueWithGoogle')}
                onClick={handleGoogleLogin}
                loading={socialLoading === 'google'}
                disabled={!!socialLoading}
              />
              
              <SocialLoginButton
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.47-2.06-.48-3.2 0-1.42.62-2.17.53-3.06-.38C3.33 15.85 4.14 8.27 9.15 8c1.2.08 2.05.56 2.81.57.76.01 1.74-.54 3.08-.61 2.05-.11 3.84.89 4.69 2.55-4.42 2.33-3.7 8.39.32 9.77ZM15.33 4.4c-1.78 0-3.21 1.6-3.13 3.13 1.73.15 3.21-1.55 3.13-3.13Z"
                    />
                  </svg>
                }
                label={t('auth.actions.signInWithApple')}
                onClick={handleAppleLogin}
                loading={socialLoading === 'apple'}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border dark:border-border-dark" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-secondary dark:text-text-dark-secondary">
                  {t('auth.actions.orWithEmail')}
                </span>
              </div>
            </div>

            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('auth.email.label')}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t('auth.email.placeholder')}
                    className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {t('auth.password.label')}
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t('auth.password.placeholder')}
                    className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                    disabled={loading}
                  />
                </div>
                <div className="mt-2 text-right">
                  <Link
                    to="/auth/forgot-password"
                    className="text-xs text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-dark dark:bg-primary-dark dark:hover:bg-primary-dark/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('auth.actions.signingIn')}
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      {t('auth.actions.signInWithEmail')}
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="text-center">
              <Link
                to="/auth/signup"
                className="text-sm text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
              >
                {t('auth.toggles.needAccount')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}